import * as React from 'react'
import { getCubeAPI } from '../../../../../../../Cube'
import { QueryRenderer } from '@cubejs-client/react'
import { Skeleton } from 'antd'
import { parseAndDownloadCSV, GrPrice } from '../../../../../../../utilities/helpers'
import { Track } from '../../../../../../../Track'
import moment from 'moment'
import { observer } from 'mobx-react'
import { BlazeButton } from '../../../../../../../components/CustomButtons/BlazeButton'
import { PaymentTypeIds, convertPaymentTypeIdToName } from '@getgreenline/homi-shared'

interface Props {
  companyId: number
  startDate: string
  endDate: string
  locationIds: number[]
}

const query = {
  measures: ['Transactions.amount'],
  timeDimensions: [],
  dimensions: ['PaymentTypes.name'],
  filters: [
    {
      dimension: 'Payments.status',
      operator: 'equals',
      values: [`completed`],
    },
  ],
  timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
}

const pennyAdjustmentQuery = {
  measures: ['Payments.pennyadjustment'],
  timeDimensions: [],
  dimensions: [],
  filters: [
    {
      dimension: 'Payments.status',
      operator: 'equals',
      values: [`completed`],
    },
  ],
  timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
}

@observer
export class PaymentTypes extends React.Component<Props> {
  // Static property that stores loaded report data
  paymentData: any
  pennyData: any

  constructor(props: Props) {
    super(props)
    this.state = {}
  }

  tableRenderer = ({ resultSet }: any) => {
    const rawData: any[] = resultSet.tablePivot()
    const total = rawData.reduce((a, b) => a + Number(b['Transactions.amount']), 0)
    if (rawData.length > 0) {
      return (
        <table className='table table-sm'>
          <tbody>
            {rawData
              .filter((data) => data['PaymentTypes.name'])
              .map((data, index) => {
                const paymentName =
                  convertPaymentTypeIdToName(Number(PaymentTypeIds[data['PaymentTypes.name']])) ||
                  data['PaymentTypes.name']
                return (
                  <tr key={index}>
                    <td>{paymentName}</td>
                    <td className='text-right'>
                      {GrPrice.convertDollarToDollar(data['Transactions.amount'])}
                    </td>
                  </tr>
                )
              })}
            <tr>
              <td>
                <b>Total</b>
              </td>
              <td className='text-right'>
                <b>{GrPrice.convertDollarToDollar(total)}</b>
              </td>
            </tr>
          </tbody>
        </table>
      )
    } else {
      return <div>No data</div>
    }
  }

  pennyTableRenderer = ({ resultSet }: any) => {
    const rawData: any[] = resultSet.tablePivot()
    if (rawData.length > 0) {
      return (
        <table className='table table-sm'>
          <tbody>
            {rawData.map((data, index) => {
              return (
                <tr key={index}>
                  <td>Penny adjustment</td>
                  <td className='text-right'>
                    {GrPrice.convertDollarToDollar(data['Payments.pennyadjustment'])}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      )
    } else {
      return <span />
    }
  }

  renderMainChart(Component: any) {
    const { startDate, endDate } = this.props!
    // eslint-disable-next-line react/prop-types
    return ({ resultSet, error }: any) => {
      if (resultSet) {
        Track.track('dashboard:reports_summaryPaymentTypes', {
          numberOfDays: moment(endDate).diff(startDate, 'days'),
        })
        // eslint-disable-next-line react/prop-types
        this.paymentData = resultSet.rawData()
      } else {
        Track.startTimer('dashboard:reports_summaryPaymentTypes')
        this.paymentData = undefined
      }
      return (
        (resultSet && <Component resultSet={resultSet} />) ||
        (error && error.toString()) || <Skeleton active />
      )
    }
  }

  renderPennyChart(Component: any) {
    const { startDate, endDate } = this.props!
    // eslint-disable-next-line react/prop-types
    return ({ resultSet, error }: any) => {
      if (resultSet) {
        Track.track('dashboard:reports_summaryPaymentTypesPenny', {
          numberOfDays: moment(endDate).diff(startDate, 'days'),
        })
        // eslint-disable-next-line react/prop-types
        this.pennyData = resultSet.rawData()
      } else {
        Track.startTimer('dashboard:reports_summaryPaymentTypesPenny')
        this.pennyData = undefined
      }
      return (
        (resultSet && <Component resultSet={resultSet} />) ||
        (error && error.toString()) || <Skeleton active />
      )
    }
  }

  exportCSV() {
    const { startDate, endDate } = this.props
    const data = [...this.paymentData, ...this.pennyData]
    if (data) {
      parseAndDownloadCSV(`payment-types-${startDate}-${endDate}`, data, [
        'PaymentTypes.name',
        'Transactions.amount',
        'Payments.pennyadjustment',
      ])
    }
  }

  render() {
    const { companyId, startDate, endDate, locationIds } = this.props

    const fullQuery = {
      ...query,
      timeDimensions: [
        {
          dimension: 'Payments.completedate',
          granularity: null,
          dateRange: [startDate, endDate],
        },
      ],
      filters: [
        ...query.filters,
        {
          dimension: 'Payments.locationId',
          operator: 'equals',
          values: locationIds.map((id) => `${id}`),
        },
      ],
    }

    const pennyFullQuery = {
      ...pennyAdjustmentQuery,
      timeDimensions: [
        {
          dimension: 'Payments.completedate',
          granularity: null,
          dateRange: [startDate, endDate],
        },
      ],
      filters: [
        ...query.filters,
        {
          dimension: 'Payments.locationId',
          operator: 'equals',
          values: locationIds.map((id) => `${id}`),
        },
      ],
    }

    return (
      <div className='w-100'>
        <div className='rounded background-white p-3 mr-3 mb-3'>
          <div>
            <b>Payment types</b>
          </div>
          <div className='text-muted'>Aggregated</div>

          <div className='mt-2 mb-2'>
            <BlazeButton
              buttonProps={{
                type: 'default',
                size: 'default',
                onClick: () => {
                  this.exportCSV()
                },
              }}
              titleProps={{
                title: 'Export as csv',
              }}
            />
          </div>

          <div>
            <QueryRenderer
              query={fullQuery}
              cubejsApi={getCubeAPI(companyId)}
              render={this.renderMainChart(this.tableRenderer)}
            />
            <br />
            <QueryRenderer
              query={pennyFullQuery}
              cubejsApi={getCubeAPI(companyId)}
              render={this.renderPennyChart(this.pennyTableRenderer)}
            />
          </div>
          <div style={{ color: '#6C757D', textAlign: 'right' }}>
            <i>Report is delayed up to 10 minutes</i>
          </div>
        </div>
      </div>
    )
  }
}
